import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input,
  Tag, Space, message, Row, Col, Statistic,
  Popconfirm, Typography, Empty
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  TeamOutlined, BankOutlined, UserOutlined
} from '@ant-design/icons';
import apiService from 'services/api/apiService';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text } = Typography;
const API = 'https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php';

const ClientManagement: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [clients, setClients]             = useState<any[]>([]);
  const [loading, setLoading]             = useState(false);
  const [modalVisible, setModalVisible]   = useState(false);
  const [editingClient, setEditingClient] = useState<any>(null);
  const [form] = Form.useForm();
  const tenantId = apiService.getTenantId();

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}?action=organizations&tenant_id=${tenantId}`);
      const data = await res.json();
      if (data.success) {
        const onlyClients = (data.data || []).filter((org: any) => org.organization_type !== 'own');
        setClients(onlyClients);
      }
    } catch (e) {
      message.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: any) => {
    try {
      const method = editingClient ? 'PUT' : 'POST';
      const body   = editingClient
        ? { action: 'update_organization', id: editingClient.id, ...values, tenant_id: tenantId }
        : { action: 'create_organization', ...values, tenant_id: tenantId };

      const res  = await fetch(`${API}?tenant_id=${tenantId}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        const isCreate = !editingClient && data.data?.client_code;
        const credEmail = values.admin_email;
        const credPassword = values.admin_password || data.data?.default_password || 'admin123';
        const credCompany = data.data?.company_name || values.company_name;
        const credCode = data.data?.client_code;

        setModalVisible(false);
        setEditingClient(null);
        form.resetFields();
        fetchClients();

        if (isCreate) {
          Modal.success({
            title: 'Client Created Successfully',
            width: 480,
            content: (
              <div>
                <p>Share these login credentials with <strong>{credCompany}</strong>:</p>
                <p><strong>Client Code:</strong> {credCode}</p>
                <p><strong>Email:</strong> {credEmail}</p>
                <p><strong>Temporary Password:</strong> {credPassword}</p>
                <p style={{ color: '#fa8c16', fontSize: 12 }}>Advise them to change their password after first login.</p>
              </div>
            ),
          });
        } else {
          message.success(editingClient ? 'Client updated' : 'Client created');
        }
      } else {
        message.error(data.error || 'Failed to save client');
      }
    } catch (e) {
      message.error('Failed to save client');
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API}?tenant_id=${tenantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'toggle_organization_status', id, status: newStatus, tenant_id: tenantId })
      });
      const data = await res.json();
      if (data.success) {
        message.success(newStatus === 'active' ? 'Client activated' : 'Client deactivated');
        fetchClients();
      } else {
        message.error(data.error || 'Failed to update status');
      }
    } catch (e) {
      message.error('Failed to update client status');
    }
  };

  const openCreate = () => {
    setEditingClient(null);
    form.resetFields();
    setModalVisible(true);
  };

  const openEdit = (record: any) => {
    setEditingClient(record);
    form.setFieldsValue({
      company_name: record.company_name,
      admin_email:  record.admin_email,
      contact_name: record.contact_name,
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Client Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (name: string) => (
        <Space><BankOutlined style={{ color:'#1890ff' }} /><Text strong>{name}</Text></Space>
      )
    },
    {
      title: 'Admin Email',
      dataIndex: 'admin_email',
      key: 'admin_email',
    },
    {
      title: 'Plan',
      dataIndex: 'plan',
      key: 'plan',
      render: (p: string) => <Tag color="blue">{(p || 'trial').toUpperCase()}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag color={s === 'active' ? 'green' : 'orange'}>{(s || 'trial').toUpperCase()}</Tag>
    },
    {
      title: 'Employees',
      dataIndex: 'employee_count',
      key: 'employee_count',
      render: (v: number) => v ?? '—'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
          <Popconfirm
            title={record.status === 'active' ? 'Deactivate this client?' : 'Reactivate this client?'}
            onConfirm={() => handleToggleStatus(record.id, record.status)}
            okText="Yes" cancelText="No" okType={record.status === 'active' ? 'danger' : 'primary'}
          >
            {record.status === 'active' ? (
              <Button size="small" icon={<DeleteOutlined />} danger>Deactivate</Button>
            ) : (
              <Button size="small" type="primary">Activate</Button>
            )}
          </Popconfirm>
        </Space>
      )
    }
  ];

  const activeClients = clients.filter(c => c.status === 'active').length;

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>Client Management</Title>
      <Text type="secondary" style={{ display:'block', marginBottom:24 }}>
        Manage your client organisations — each client has isolated HR data
      </Text>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom:24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Total Clients" value={clients.length} prefix={<TeamOutlined />} valueStyle={{ color:'#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Active Clients" value={activeClients} prefix={<UserOutlined />} valueStyle={{ color:'#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic title="Trial Clients" value={clients.length - activeClients} prefix={<BankOutlined />} valueStyle={{ color:'#faad14' }} />
          </Card>
        </Col>
      </Row>

      {/* Client table */}
      <Card
        title="Client Organisations"
        extra={
          isAdmin() && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add Client
            </Button>
          )
        }
      >
        <Table
          columns={columns}
          dataSource={clients}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: <Empty description="No clients yet. Add your first client organisation." /> }}
        />
      </Card>

      {/* Create / Edit Modal */}
      <Modal
        title={editingClient ? 'Edit Client' : 'Add New Client'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditingClient(null); form.resetFields(); }}
        footer={null}
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="company_name" label="Company Name"
            rules={[{ required:true, message:'Please enter company name' }]}>
            <Input placeholder="e.g. Acme Nigeria Limited" />
          </Form.Item>
          <Form.Item name="admin_email" label="Admin Email"
            rules={[{ required:true, message:'Please enter admin email' }, { type:'email' }]}>
            <Input placeholder="admin@client.com" />
          </Form.Item>
          {!editingClient && (
            <Form.Item name="admin_password" label="Temporary Password"
              rules={[{ required:true, message:'Please set a temporary password' }, { min:8 }]}>
              <Input.Password placeholder="Min 8 characters" />
            </Form.Item>
          )}
          <Form.Item name="contact_name" label="Contact Person">
            <Input placeholder="Primary contact name" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingClient ? 'Update Client' : 'Create Client'}
              </Button>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); }}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ClientManagement;
